/**
 * useCrossTabSession hook for managing session state across browser tabs.
 *
 * Features:
 * - Each tab maintains its own session via Keycloak
 * - Handles cross-tab logout synchronization
 * - Refreshes auth state on tab focus if stale
 * - Uses BroadcastChannel API for cross-tab communication
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  isAuthenticated,
  isLoggingOut,
  isPublicRoute,
  refreshToken,
  SESSION_CHANNEL_NAME,
} from '@/lib/keycloak';

/**
 * Session event types for cross-tab communication
 */
export type SessionEventType = 'logout' | 'login' | 'token-refresh';

/**
 * Session event payload
 */
export interface SessionEvent {
  type: SessionEventType;
  timestamp: number;
  tabId: string;
}

/** Generate a unique tab ID */
function generateTabId(): string {
  return `tab-${String(Date.now())}-${Math.random().toString(36).substring(2, 11)}`;
}

/** Staleness threshold in milliseconds (5 minutes) */
const STALE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Options for the useCrossTabSession hook
 */
export interface UseCrossTabSessionOptions {
  /** Whether to automatically refresh on tab focus (default: true) */
  refreshOnFocus?: boolean;
  /** Custom staleness threshold in ms (default: 5 minutes) */
  staleThreshold?: number;
  /** Callback when logout occurs from another tab */
  onCrossTabLogout?: () => void;
  /** Callback when auth state is refreshed */
  onAuthRefreshed?: () => void;
}

/**
 * Return type for the useCrossTabSession hook
 */
export interface UseCrossTabSessionReturn {
  /** Current tab's unique ID */
  tabId: string;
  /** Whether the session was just refreshed from another tab */
  wasRefreshed: boolean;
  /** Broadcast a logout event to other tabs */
  broadcastLogout: () => void;
  /** Broadcast a token refresh event to other tabs */
  broadcastTokenRefresh: () => void;
  /** Manually check and refresh if stale */
  checkAndRefresh: () => Promise<boolean>;
}

/**
 * Hook for managing session state across browser tabs.
 *
 * This hook ensures consistent session state across multiple browser tabs
 * by synchronizing logout events and refreshing auth state when tabs
 * become active after being in the background.
 *
 * @param options - Configuration options
 * @returns Cross-tab session management utilities
 *
 * @example
 * ```tsx
 * function AppLayout() {
 *   const { broadcastLogout } = useCrossTabSession({
 *     onCrossTabLogout: () => {
 *       // Handle logout initiated from another tab
 *       toast.info('You were logged out from another tab');
 *     },
 *   });
 *
 *   const handleLogout = () => {
 *     broadcastLogout();
 *     logout();
 *   };
 *
 *   return <MainLayout />;
 * }
 * ```
 */
export function useCrossTabSession(
  options: UseCrossTabSessionOptions = {}
): UseCrossTabSessionReturn {
  const {
    refreshOnFocus = true,
    staleThreshold = STALE_THRESHOLD_MS,
    onCrossTabLogout,
    onAuthRefreshed,
  } = options;

  // Generate unique tab ID on mount
  const tabIdRef = useRef(generateTabId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastActiveRef = useRef(Date.now());

  const [wasRefreshed, setWasRefreshed] = useState(false);

  // Selector-based subscriptions. Reading the whole store (`useAuthStore()`)
  // subscribes this hook to EVERY auth store write, and this hook is mounted at
  // the application root - useAuth refreshes the token on a 30 second interval
  // and updateFromKeycloak calls set() unconditionally, so a whole-store
  // subscription re-renders the entire app tree on a timer, forever. Zustand
  // action identities are stable, so these two are safe to close over.
  const updateFromKeycloak = useAuthStore((state) => state.updateFromKeycloak);
  const authStoreLogout = useAuthStore((state) => state.logout);

  // Consumer callbacks are held in refs so the channel effect below can use a
  // stable, empty dependency array. Mounted with an inline arrow (the natural
  // call shape) the callback identity changes on every render of the host
  // component; with the callbacks in the dependency array that closed and
  // reopened the BroadcastChannel on every render, and a logout message
  // arriving in the gap between close() and the new channel was dropped -
  // exactly the failure A.08 exists to close.
  const onCrossTabLogoutRef = useRef(onCrossTabLogout);
  const onAuthRefreshedRef = useRef(onAuthRefreshed);
  useEffect(() => {
    onCrossTabLogoutRef.current = onCrossTabLogout;
    onAuthRefreshedRef.current = onAuthRefreshed;
  }, [onCrossTabLogout, onAuthRefreshed]);

  /**
   * Initialize BroadcastChannel for cross-tab communication
   */
  useEffect(() => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('BroadcastChannel not supported, cross-tab sync disabled');
      return;
    }

    try {
      channelRef.current = new BroadcastChannel(SESSION_CHANNEL_NAME);

      // Handle incoming messages from other tabs
      channelRef.current.onmessage = (event: MessageEvent<SessionEvent>) => {
        const { type, tabId } = event.data;

        // Ignore events from this tab
        if (tabId === tabIdRef.current) {
          return;
        }

        switch (type) {
          case 'logout':
            // This tab has already started signing itself out. Reacting again
            // would start a second redirect and re-broadcast, which is how two
            // tabs end up ping-ponging logout messages.
            if (isLoggingOut()) {
              break;
            }
            // A guest reading a shared chat, a meetup poll or an RSVP page was
            // never signed in here. authStoreLogout() runs a full RP-initiated
            // Keycloak logout redirect, so without this guard an unrelated
            // tab's sign out would yank a guest off the page they are reading.
            if (isPublicRoute() || !isAuthenticated()) {
              break;
            }
            // Another tab logged out - sync local state
            console.log('Cross-tab logout detected, syncing state');
            authStoreLogout();
            onCrossTabLogoutRef.current?.();
            break;

          case 'login':
          case 'token-refresh':
            // Another tab refreshed - update our state
            console.log('Cross-tab token refresh detected, updating state');
            updateFromKeycloak();
            setWasRefreshed(true);
            onAuthRefreshedRef.current?.();
            // Clear the flag after a short delay
            setTimeout(() => {
              setWasRefreshed(false);
            }, 100);
            break;
        }
      };

      channelRef.current.onmessageerror = (error) => {
        console.error('BroadcastChannel message error:', error);
      };
    } catch (error) {
      console.error('Failed to create BroadcastChannel:', error);
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [authStoreLogout, updateFromKeycloak]);

  /**
   * Broadcast a session event to other tabs
   */
  const broadcastEvent = useCallback((type: SessionEventType) => {
    if (!channelRef.current) {
      return;
    }

    const event: SessionEvent = {
      type,
      timestamp: Date.now(),
      tabId: tabIdRef.current,
    };

    try {
      channelRef.current.postMessage(event);
    } catch (error) {
      console.error('Failed to broadcast session event:', error);
    }
  }, []);

  /**
   * Broadcast logout event to other tabs
   */
  const broadcastLogout = useCallback(() => {
    broadcastEvent('logout');
  }, [broadcastEvent]);

  /**
   * Broadcast token refresh event to other tabs
   */
  const broadcastTokenRefresh = useCallback(() => {
    broadcastEvent('token-refresh');
  }, [broadcastEvent]);

  /**
   * Check if session is stale and refresh if needed
   */
  const checkAndRefresh = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    const timeSinceLastActive = now - lastActiveRef.current;

    // Update last active time
    lastActiveRef.current = now;

    // Only refresh if we've been inactive longer than the threshold
    if (timeSinceLastActive < staleThreshold) {
      return false;
    }

    // Check if we're still authenticated
    if (!isAuthenticated()) {
      return false;
    }

    try {
      // Attempt to refresh the token
      const refreshed = await refreshToken(60); // 1 minute validity

      if (refreshed) {
        updateFromKeycloak();
        broadcastTokenRefresh();
        onAuthRefreshedRef.current?.();
        return true;
      }
    } catch (error) {
      console.warn('Failed to refresh token on tab focus:', error);
    }

    return false;
  }, [staleThreshold, updateFromKeycloak, broadcastTokenRefresh]);

  /**
   * Handle tab visibility change
   */
  useEffect(() => {
    if (!refreshOnFocus) {
      return;
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check and refresh if stale
        void checkAndRefresh();
      } else {
        // Tab became hidden - record last active time
        lastActiveRef.current = Date.now();
      }
    }

    function handleFocus() {
      // Also handle window focus (for when visibility API doesn't fire)
      void checkAndRefresh();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshOnFocus, checkAndRefresh]);

  // A `storage`-event fallback used to live here: it re-broadcast a logout
  // whenever localStorage['auth-storage'] was removed. It is deliberately gone.
  // The key IS populated (zustand's persist writes the envelope {"state":{}}),
  // but clearAllUserData never removes it, so the effect could never fire. More
  // importantly, if it ever did fire it would fire in EVERY receiving tab at
  // once and each would re-broadcast to every other tab - the message
  // amplification the single broadcast in keycloak.logout() exists to avoid.
  // The broadcast now has exactly one origin: keycloak.logout().

  return {
    tabId: tabIdRef.current,
    wasRefreshed,
    broadcastLogout,
    broadcastTokenRefresh,
    checkAndRefresh,
  };
}

export default useCrossTabSession;
